/**
 * The words that go in the text, and whose turn it is to receive one.
 *
 * Two rules shape everything here, and both are about what a text from a
 * person looks like.
 *
 * **Nothing is written by a model.** The templates are fixed and the merge
 * fields are the only thing that varies. This was asked for explicitly: every
 * message has to be readable in a review queue before it goes, and a message
 * generated per partner is a message nobody has read. Editing happens by hand,
 * in the queue, one message at a time.
 *
 * **A rendered message is frozen.** `partner_messages.body` holds the final
 * words with the merge fields already resolved, and it is never re-rendered.
 * The template can be rewritten tomorrow; what a partner was sent has to keep
 * saying what it said.
 *
 * The renderer below is also transcribed into SQL, in the migration that adds
 * `draft_partner_batch`, because the drafting is an invariant and CI cannot run
 * Deno. `supabase/test/partner-message-render-match.test.ts` binds the two: if
 * they ever disagree about a single character, that test fails.
 */

import {
  partnerCategoryNoun,
  partnerFirstName,
  type PartnerName,
} from "./partners.js";

/* ----------------------------------------------------------- merge fields -- */

/**
 * Every placeholder a template may use.
 *
 * Listed rather than inferred so the template editor can show them, and so an
 * unknown one is a fact somebody can be told about rather than a hole that
 * appears in a partner's text.
 */
export const PARTNER_MERGE_FIELDS = [
  "first_name",
  "rep_first_name",
  "county",
  "storm_summary",
  "storm_report_url",
  "customer_name",
  "customer_phone",
  "one_line_context",
  "category_noun",
  "referrals_from_count",
  "referrals_to_count",
] as const;

export type PartnerMergeField = (typeof PARTNER_MERGE_FIELDS)[number];

export type PartnerMergeValues = Partial<Record<PartnerMergeField, string>>;

/**
 * A placeholder, exactly as it is written in a template body.
 *
 * `{{ first_name }}` with spaces is accepted as well as `{{first_name}}`,
 * because a template is edited in a plain textarea by somebody who is not a
 * developer and a stray space should not put `{{ first_name }}` into a
 * customer's text message.
 */
const PLACEHOLDER = /\{\{\s*([a-z_]+)\s*\}\}/g;

/**
 * Fill a template in.
 *
 * An unknown or missing field renders as the empty string, and then the
 * whitespace around it is tidied. That combination matters more than it looks:
 * "Dana — — got a client for you" is what a naive substitution produces when
 * one field is blank, and it is the kind of thing that goes out to forty people
 * before anybody notices. Collapsing runs of spaces and stripping a dangling
 * separator turns a missing field into a slightly shorter sentence instead of a
 * visibly broken one.
 *
 * It does not, however, make a missing field *safe* — a storm text with no
 * summary in it says nothing. `missingMergeFields` is how the review queue
 * catches that, and the queue is where a person catches it.
 */
export function renderPartnerMessage(
  body: string,
  values: PartnerMergeValues,
): string {
  const filled = body.replace(PLACEHOLDER, (_match, field: string) => {
    const value = values[field as PartnerMergeField];
    return value === undefined || value === null ? "" : value;
  });

  return (
    filled
      // A separator left stranded by a blank field: "Dana —  got a client".
      .replace(/\s+—\s+—\s+/g, " — ")
      // Runs of spaces and tabs, but never newlines: a template that lays a
      // message out over two lines meant to.
      .replace(/[ \t]{2,}/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/ +([,.!?])/g, "$1")
      .trim()
  );
}

/**
 * Which placeholders the template wanted and the values did not supply.
 *
 * Used by the review queue to flag a draft before anybody approves it. A
 * partner with no first name on their card, or a storm event whose summary has
 * not been written yet, both surface here rather than in somebody's inbox.
 */
export function missingMergeFields(
  body: string,
  values: PartnerMergeValues,
): PartnerMergeField[] {
  const missing = new Set<PartnerMergeField>();
  for (const match of body.matchAll(PLACEHOLDER)) {
    const field = match[1] as PartnerMergeField;
    const value = values[field];
    if (value === undefined || value === null || value.trim() === "") {
      missing.add(field);
    }
  }
  return [...missing];
}

/**
 * Placeholders in a template that this application does not know how to fill.
 *
 * A typo in the template editor — `{{firstname}}` — would otherwise render as
 * nothing and read as a template that simply forgot to greet anybody.
 */
export function unknownMergeFields(body: string): string[] {
  const known = new Set<string>(PARTNER_MERGE_FIELDS);
  const unknown = new Set<string>();
  for (const match of body.matchAll(PLACEHOLDER)) {
    if (!known.has(match[1]!)) unknown.add(match[1]!);
  }
  return [...unknown];
}

/* --------------------------------------------------------- the merge data -- */

export interface PartnerForMessage extends PartnerName {
  category?: string | null;
  county?: string | null;
}

export interface StormForMessage {
  summary?: string | null;
  report_url?: string | null;
  county?: string | null;
}

export interface MergeContext {
  partner: PartnerForMessage;
  repFirstName?: string | null;
  storm?: StormForMessage | null;
  customerName?: string | null;
  customerPhone?: string | null;
  oneLineContext?: string | null;
  referralsFromCount?: number;
  referralsToCount?: number;
}

const COUNTY_IN_A_SENTENCE: Record<string, string> = {
  shelby: "Shelby County",
  jefferson: "Jefferson County",
};

/**
 * Turn what we know into what the template asked for.
 *
 * The county is the storm's, not the partner's, whenever there is a storm:
 * an adjuster in Shelby reading about Jefferson needs the sentence to say
 * Jefferson, or the offer in it is for the wrong place.
 */
export function mergeValues(context: MergeContext): PartnerMergeValues {
  const county = context.storm?.county ?? context.partner.county ?? "";
  return {
    first_name: partnerFirstName(context.partner),
    rep_first_name: context.repFirstName?.trim() ?? "",
    county: COUNTY_IN_A_SENTENCE[county] ?? "",
    storm_summary: context.storm?.summary?.trim() ?? "",
    storm_report_url: context.storm?.report_url?.trim() ?? "",
    customer_name: context.customerName?.trim() ?? "",
    customer_phone: context.customerPhone?.trim() ?? "",
    one_line_context: context.oneLineContext?.trim() ?? "",
    category_noun: partnerCategoryNoun(context.partner.category),
    referrals_from_count: String(context.referralsFromCount ?? 0),
    referrals_to_count: String(context.referralsToCount ?? 0),
  };
}

/* -------------------------------------------------------------- rotation -- */

export interface RotationTemplate {
  id: string;
  category: string | null;
  sort_order: number;
  active?: boolean;
}

/**
 * Whose turn it is, for this partner.
 *
 * The rule asked for is "don't repeat until the rotation is exhausted", and the
 * obvious implementation — remember the last one sent and take the next — turns
 * out to be wrong in two ordinary situations. A template added halfway through
 * the year would never be reached until the list wrapped, and a template
 * deactivated and reactivated would jump the queue.
 *
 * So: **fewest times used wins, ties broken by sort order.** That produces the
 * same sequence as "next in the list" while the set of templates is stable,
 * restarts by itself once everything has been used once, and gives a
 * newly-added template its turn immediately — which is what somebody who has
 * just written one expects.
 *
 * Category-specific templates only ever reach their category. A general
 * template reaches everybody, including that category, so a realtor's rotation
 * is the general ones plus the realtor one rather than the realtor one alone.
 */
export function nextRotationTemplate(
  templates: readonly RotationTemplate[],
  category: string | null | undefined,
  usageById: ReadonlyMap<string, number>,
): RotationTemplate | null {
  const eligible = templates.filter(
    (template) =>
      template.active !== false &&
      (template.category === null || template.category === category),
  );
  if (eligible.length === 0) return null;

  let best = eligible[0]!;
  let bestUses = usageById.get(best.id) ?? 0;
  for (const template of eligible.slice(1)) {
    const uses = usageById.get(template.id) ?? 0;
    if (
      uses < bestUses ||
      (uses === bestUses && template.sort_order < best.sort_order) ||
      (uses === bestUses &&
        template.sort_order === best.sort_order &&
        template.id < best.id)
    ) {
      best = template;
      bestUses = uses;
    }
  }
  return best;
}

/* --------------------------------------------------------------- lengths -- */

/**
 * One SMS segment, in the encoding a message with an em dash forces.
 *
 * Every template here uses "—", which is not in the GSM-7 alphabet, so these
 * messages are sent as UCS-2 and a segment is 70 characters rather than 160.
 * That is not a problem — a long text arrives as one message on any modern
 * handset — but the character count above the textarea has to be counting the
 * right thing, or an editor trims a message to 160 for no reason.
 */
export const SMS_SEGMENT_UCS2 = 70;
export const SMS_SEGMENT_GSM7 = 160;

/**
 * A rough segment count, for the counter above the editor.
 *
 * Rough is the right word and the comment is here so nobody "fixes" it: exact
 * segmentation depends on the handset, the carrier and which extended
 * characters appear, and none of that changes any decision anyone makes on
 * this screen. What the counter is for is telling somebody that a message they
 * are editing has grown from three segments to nine.
 */
export function smsSegments(body: string): number {
  const text = body.trim();
  if (!text) return 0;
  // Anything outside the printable ASCII range forces UCS-2 in practice.
  const unicode = /[^\x20-\x7E\n\r]/.test(text);
  const size = unicode ? SMS_SEGMENT_UCS2 : SMS_SEGMENT_GSM7;
  return Math.ceil([...text].length / size);
}
